// DocumentViewer.jsx — look at the document, wherever it came from.
//
// One viewer for both halves of this package, because they are the same
// question asked twice: "View" on an invoice asks what the client will be
// billed with, and the attachment link on a reminder asks what the client is
// about to receive. Both answers are a file behind a JWT.
//
// WHY IT IS FETCHED RATHER THAN LINKED. These documents are served as bytes on
// authenticated routes, and an <a href> carries no Authorization header. The
// alternative — a token in the query string — would put a live credential into
// browser history, server logs and every Referer the page emits. So the bytes
// are fetched with the header and wrapped in a blob: URL, which is scoped to
// this tab and revoked when the panel closes.
//
// TWO KINDS OF DOCUMENT, ONE FRAME. An uploaded invoice is a PDF and renders
// in the browser's own viewer; a built one is self-contained print-ready HTML
// and renders as the page it is. Both go in the same sandboxed iframe, so the
// operator never has to know which kind they are looking at — which is the
// whole point: an invoice is an invoice however it got here.

import React, { useCallback, useEffect, useRef, useState } from "react";

// HTML is sandboxed WITHOUT allow-scripts. A blob: URL inherits this origin,
// so a compiled invoice carrying a <script> — anything in a client name, an
// address or a line-item description ends up in that document — would run
// against the operator's session. Without allow-scripts it cannot.
//
// A PDF is not sandboxed, because a sandboxed frame blocks the browser's own
// PDF viewer and the panel would render an empty box. Nothing is lost: the
// bytes are handed to a viewer that does not execute the document's script.
const HTML_FRAME_SANDBOX = "allow-same-origin";

const isPdf = (type) => /application\/pdf/i.test(String(type || ""));

/**
 * @param {string}   title    what is being looked at, in the operator's words
 * @param {Function} load     () => Promise<{ blob, contentType, filename }>
 * @param {string}   note     optional line under the header (where the file came from)
 * @param {Function} onClose
 * @param {number}   zIndex   raise it above whatever opened it
 */
export default function DocumentViewer({ title, load, note, onClose, zIndex = 60 }) {
  const [state, setState] = useState({ status: "loading", url: "", type: "", filename: "", error: "" });
  // The blob URL is a resource, not state: it must be revoked exactly once,
  // including when the panel is closed mid-fetch.
  const urlRef = useRef("");
  // `load` is a closure the caller rebuilds on every one of ITS renders, and
  // the parent re-renders for reasons that have nothing to do with this panel.
  // Depending on it would refetch the document each time and leak a blob per
  // pass, so it is held in a ref and the fetch runs once per mount — callers
  // give this component a `key` when the document changes.
  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { blob, contentType, filename } = await loadRef.current();
        const objectUrl = URL.createObjectURL(blob);
        if (cancelled) { URL.revokeObjectURL(objectUrl); return; }
        urlRef.current = objectUrl;
        setState({
          status: "ready",
          url: objectUrl,
          type: contentType || blob.type || "",
          filename: filename || "document",
          error: "",
        });
      } catch (e) {
        if (!cancelled) {
          setState({ status: "error", url: "", type: "", filename: "", error: (e && e.message) || "Could not open this document" });
        }
      }
    })();
    return () => {
      cancelled = true;
      if (urlRef.current) { URL.revokeObjectURL(urlRef.current); urlRef.current = ""; }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Its own tab, so the browser's Print / Save-as-PDF is available without this
  // page having to render one — the same escape hatch the edit preview offers.
  const openInTab = useCallback(() => {
    if (state.url) window.open(state.url, "_blank", "noopener,noreferrer");
  }, [state.url]);

  return (
    <div
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex, display: "flex", alignItems: "center", justifyContent: "center" }}
      onClick={onClose}
    >
      <div
        style={{ background: "#fff", width: 980, maxWidth: "96vw", height: "92vh", display: "flex", flexDirection: "column", borderRadius: 8, padding: 18 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="d-flex align-items-baseline justify-content-between mb-2" style={{ gap: 12 }}>
          <div style={{ minWidth: 0 }}>
            <h5 className="mb-0" style={{ wordBreak: "break-word" }}>{title}</h5>
            {(note || state.filename) && (
              <div style={{ fontSize: 11, color: "#8a949c", marginTop: 2 }}>
                {state.filename}{note ? ` · ${note}` : ""}
              </div>
            )}
          </div>
          <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
            {state.status === "ready" && (
              <>
                <button className="acc-btn" onClick={openInTab}>Open / print</button>
                {/* Downloading is a separate act from looking, and the filename
                    the server chose is the one that lands in Downloads. */}
                <a
                  className="acc-btn"
                  href={state.url}
                  download={state.filename || "invoice"}
                  style={{ textDecoration: "none", lineHeight: "26px" }}
                >
                  Download
                </a>
              </>
            )}
            <button className="acc-btn" onClick={onClose}>Close</button>
          </div>
        </div>

        {state.status === "loading" && <div className="cm-empty" style={{ flex: 1 }}>Opening the document…</div>}

        {state.status === "error" && (
          <div className="alert alert-warning" style={{ fontSize: 13 }}>
            {state.error}
            {/* The one failure worth naming, because it is a real state and not
                a fault: a built invoice that has never been compiled. */}
            <div style={{ fontSize: 11, color: "#8a949c", marginTop: 6 }}>
              If this is an invoice built from line items, open <b>Edit</b> and save it once to compile its document.
            </div>
          </div>
        )}

        {state.status === "ready" && (
          <iframe
            title={title}
            src={state.url}
            sandbox={isPdf(state.type) ? undefined : HTML_FRAME_SANDBOX}
            style={{ flex: 1, width: "100%", border: "1px solid #e6e9ec", borderRadius: 4, background: "#fff" }}
          />
        )}
      </div>
    </div>
  );
}
